/*! Optimized Lazy Image & Progressive Picture */

// ---------- helpers ----------
const _swapDataToLive = (el) => {
  const ds = el.getAttribute('data-srcset');
  if (ds) el.setAttribute('srcset', ds);
  const s = el.getAttribute('data-src');
  if (s && el.tagName === 'IMG') el.setAttribute('src', s);
};
const _decodeSafe = (img) => ('decode' in img ? img.decode() : Promise.resolve());
const _inSmallView = () => !matchMedia('(min-width: 750px)').matches;

// ---------- LazyImage ----------
class LazyImage extends HTMLImageElement {
  constructor() {
    super();
    this.wrapper = this.closest('.media-wrapper, .media, picture') || this.parentElement;
    if (!this.wrapper) return;

    if (!this.hasAttribute('loading')) this.setAttribute('loading', 'lazy');
    if (!this.hasAttribute('decoding')) this.setAttribute('decoding', 'async');
    this.classList.remove('loaded');

    this._mo = new MutationObserver((changes) => {
      for (const c of changes) {
        if (c.attributeName && /^(data-)?src(set)?$/.test(c.attributeName)) {
          this._observeOrHydrate();
          break;
        }
      }
    });
    this._mo.observe(this, { attributes: true });

    this._onResize = this._onResize.bind(this);
    addEventListener('resize', this._onResize, { passive: true });

    this._observeOrHydrate();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  _cleanup() {
    this._mo?.disconnect();
    removeEventListener('resize', this._onResize);
    this._io?.disconnect();
  }

  _onResize() {
    if (!_inSmallView() && this.classList.contains('medium-hide')) return;
    if (_inSmallView() && this.classList.contains('small-hide')) return;
    const cur = this.getAttribute('srcset');
    if (cur) this.setAttribute('srcset', cur);
  }

  _observeOrHydrate() {
    if (!_inSmallView() && this.classList.contains('medium-hide')) return;
    if (_inSmallView() && this.classList.contains('small-hide')) return;

    if ((this.complete && this.currentSrc) || this.classList.contains('loaded')) return;

    if ('IntersectionObserver' in window) {
      this._io?.disconnect?.();
      this._io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            this._io.disconnect();
            this._lazyLoad();
          }
        }
      }, { rootMargin: '300px 0px', threshold: 0.01 });
      this._io.observe(this);
    } else {
      this._lazyLoad();
    }
  }

  async _lazyLoad() {
    if (this._hydrated) return;
    this._hydrated = true;

    this.wrapper.classList.add('loading');

    const pic = this.closest('picture');
    if (pic) pic.querySelectorAll('source').forEach(_swapDataToLive);
    _swapDataToLive(this);

    try {
      await _decodeSafe(this);
      this.classList.add('loaded');
      this.wrapper.classList.remove('loading');
    } catch {
      this.wrapper.classList.remove('loading');
    }
  }
}
try {
  window.customElements.define('lazy-image', LazyImage, { extends: 'img' });
} catch {}

// ---------- ProgPicture ----------
class ProgPicture extends HTMLPictureElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this._maybeBindHQ();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  _maybeBindHQ() {
    const hq = this.getAttribute('data-hq');
    if (!hq) return;

    const signal = this.abortController.signal;
    const doSwap = () => {
      const firstSource = this.querySelector('source');
      if (!firstSource) return;
      firstSource.setAttribute('srcset', hq);
      const img = this.querySelector('img');
      if (img && img.getAttribute('srcset')) {
        img.setAttribute('srcset', img.getAttribute('srcset'));
      }
      this.abortController.abort();
    };

    if ('visualViewport' in window) {
      const vv = window.visualViewport;
      const onVV = () => { if ((vv.scale || 1) > 1.01) doSwap(); };
      vv.addEventListener('resize', onVV, { signal });
      vv.addEventListener('scroll', onVV, { signal });
    }

    let lastDPR = window.devicePixelRatio || 1;
    const onResize = () => {
      const now = window.devicePixelRatio || 1;
      if (now - lastDPR > 0.1) doSwap();
      lastDPR = now;
    };
    addEventListener('resize', onResize, { signal, passive: true });

    const onGesture = (ev) => { if (ev.scale && ev.scale > 1.01) doSwap(); };
    addEventListener('gesturestart', onGesture, { signal });
    addEventListener('gesturechange', onGesture, { signal });

    this.addEventListener('touchmove', () => {
      if ('visualViewport' in window && window.visualViewport.scale > 1.01) doSwap();
    }, { signal });
  }
}
try {
  window.customElements.define('prog-picture', ProgPicture, { extends: 'picture' });
} catch {}
